import HttpMethod from "http-method-enum";
import { get, http } from "./HttpService";
import {
  SEARCH_INDEX_API_KEY,
  SEARCH_FACETS,
  PARTNER_API_BASE,
  PORTAL_API_BASE,
  CP_UAM_SUBSCRIPTION_KEY,
  PARTNER_API_CLIENT_ID,
  CATERING_API_BASE_URL,
  CATERING_API_CLIENT_ID,
  CATERING_API_CLIENT_SECRET,
} from "../configs/AppConfig";
import moment from "moment";
import store from "../redux/store";
import { OrderStatus } from "constants/OrderStatusConstant";

const ApiService = {};

ApiService.getAirports = async (params) => {
  const { currentPage, pageSize, ...restOfParams } = params;
  const response = await get("airports", {
    header: {
      page: currentPage,
      "per-page": pageSize,
    },
    restOfParams,
  });
  return response;
};

ApiService.searchAirports = async (params) => {
  const { key, value, currentPage, pageSize } = params;
  const response = await get("airports", {
    header: {
      page: currentPage,
      "per-page": pageSize,
    },
    params: {
      filterType: key,
      filterValue: value,
    },
  });
  return response;
};

ApiService.getAirportDetails = async (params) => {
  const { key, value, currentPage, pageSize } = params;
  const response = await get("fbos", {
    header: {
      page: currentPage,
      "per-page": pageSize,
    },
    params: {
      filterType: key,
      filterValue: value,
    },
  });
  return response;
};

ApiService.getMenus = async (params) => {
  const { currentPage, pageSize, partnerId } = params;
  const response = await get("menu", {
    header: {
      page: currentPage,
      "per-page": pageSize,
    },
    params: {
      partnerId: partnerId,
      requires: "externalId",
    },
  });
  return response;
};

ApiService.getOrders = async (params) => {
  const { currentPage, pageSize, search, token, filter, sortField, sortOrder } =
    params;
  let apiUrl = `${PARTNER_API_BASE}/orders?$count=true&${SEARCH_FACETS}`;

  if (search) apiUrl = `${apiUrl}&search=${search}`;
  if (filter) apiUrl = `${apiUrl}&$filter=${filter}`;

  if (sortField) {
    apiUrl = `${apiUrl}&$orderby=${sortField} ${sortOrder}`;
  } else {
    apiUrl = `${apiUrl}&$orderby=deliverydatetime desc`;
  }

  apiUrl = `${apiUrl}&$skip=${(currentPage - 1) * pageSize}&queryType=full`;

  return await http({
    url: apiUrl,
    method: "GET",
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.getOrderById = async (params) => {
  const { orderId, token } = params;
  return await http({
    url: `${PARTNER_API_BASE}/orders?search=${orderId}`,
    method: "GET",
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.getVendorOrders = async (params) => {
  const { search, token, filter } = params;
  let baseUrl = `${PARTNER_API_BASE}/orders?$count=true&facet=airport/icao,count:0,sort:value&facet=fbo/name,count:0,sort:value`;

  if (search) baseUrl = `${baseUrl}&search=${search}`;
  if (filter) baseUrl = `${baseUrl}&$filter=${filter}`;

  baseUrl = `${baseUrl}&$orderby=utcDeliveryDateTime asc`;

  const pageSize = 1000;
  let skip = 0;

  let allOrders = [];
  let totalCount = 0;
  let facets = {};
  let contextUrl = null;
  let finalStatus = 200;

  while (true) {
    const apiUrl = `${baseUrl}&$skip=${skip}&$top=${pageSize}`;
    const response = await http({
      url: apiUrl,
      method: "GET",
      header: {
        "api-key": SEARCH_INDEX_API_KEY,
        Authorization: `Bearer ${token}`,
      },
    });

    const { status, data } = response;

    if (status !== 200) {
      return {
        status,
        data,
      };
    }

    // Capture metadata from first response
    if (skip === 0) {
      finalStatus = status;
      totalCount = data?.["@odata.count"] || 0;
      contextUrl = data?.["@odata.context"] || null;
      facets = data?.["@search.facets"] || {};
    }

    const records = data?.value || [];
    allOrders = allOrders.concat(records);

    skip += pageSize;
    if (skip >= totalCount || records.length < pageSize) break;
  }

  // ✅ Keep same structure as original
  return {
    status: finalStatus,
    data: {
      "@odata.context": contextUrl,
      "@odata.count": totalCount,
      "@search.facets": facets,
      value: allOrders,
    },
  };
};

ApiService.getVendorDashboardOrders = async (params) => {
  const { search, token, filter, pageIndex = 0, pageSize = 25 } = params;
  let baseUrl = `${PARTNER_API_BASE}/orders?$count=true&facet=airport/icao,count:0,sort:value&facet=fbo/name,count:0,sort:value&facet=supplier/friendlyName,count:0,sort:value&facet=salesName,count:0,sort:value&facet=orderStatus,count:0,sort:value`;

  if (search) baseUrl = `${baseUrl}&search=${search}`;
  if (filter) baseUrl = `${baseUrl}&$filter=${filter}`;

  baseUrl = `${baseUrl}&$orderby=utcDeliveryDateTime asc`;
  const skip = pageIndex * pageSize;

  return await http({
    url: `${baseUrl}&$skip=${skip}&$top=${pageSize}`,
    method: "GET",
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.getDashboardKitchenOrders = async (params) => {
  const { search, searchField = "default", token, filter } = params;
  let baseUrl = `${PARTNER_API_BASE}/orders?$count=true&facet=currency,count:0,sort:value&facet=airport/icao,count:0,sort:value&facet=fbo/name,count:0,sort:value&facet=supplier/friendlyName,count:0,sort:value&facet=modeofDelivery,count:0,sort:value`;

  if (search) {
    baseUrl =
      searchField === "salesOrderId"
        ? `${baseUrl}&search=/.*${search.trim()}/&searchFields=salesOrderId&queryType=full`
        : `${baseUrl}&search=${search}`;
  }
  if (filter) baseUrl = `${baseUrl}&$filter=${filter}`;

  baseUrl = `${baseUrl}&$orderby=utcDeliveryDateTime asc`;

  const pageSize = 1000;
  let skip = 0;

  let allOrders = [];
  let totalCount = 0;
  let facets = {};
  let contextUrl = null;
  let finalStatus = 200;

  while (true) {
    const apiUrl = `${baseUrl}&$skip=${skip}&$top=${pageSize}`;
    const response = await http({
      url: apiUrl,
      method: "GET",
      header: {
        "api-key": SEARCH_INDEX_API_KEY,
        Authorization: `Bearer ${token}`,
      },
    });

    const { status, data } = response;

    if (status !== 200) {
      return {
        status,
        data,
      };
    }

    // Capture metadata from first response
    if (skip === 0) {
      finalStatus = status;
      totalCount = data?.["@odata.count"] || 0;
      contextUrl = data?.["@odata.context"] || null;
      facets = data?.["@search.facets"] || {};
    }

    const records = data?.value || [];
    allOrders = allOrders.concat(records);

    skip += pageSize;
    if (skip >= totalCount || records.length < pageSize) break;
  }

  // ✅ Keep same structure as original
  return {
    status: finalStatus,
    data: {
      "@odata.context": contextUrl,
      "@odata.count": totalCount,
      "@search.facets": facets,
      value: allOrders,
    },
  };
};

ApiService.getKitchenOrders = async (params) => {
  const {
    token,
    startDate,
    endDate,
    kitchen,
    type,
    page,
    pageSize,
    search,
    sorting,
  } = params;
  let lessOperator = "le";
  let sdate = startDate;
  let edate = endDate;
  if (startDate === "" || startDate === undefined) {
    sdate = moment().format("YYYY-MM-DD");
  }

  if (endDate === "" || startDate === endDate) {
    edate = moment(sdate).add("days", 1).format("YYYY-MM-DD");
    lessOperator = "lt";
  }

  let filter = `(deliverydatetime ge '${sdate}' and deliverydatetime ${lessOperator} '${edate}')`;
  if (kitchen !== null)
    filter = `${filter} and supplier/friendlyName eq '${kitchen}'`;
  if (type === "ORDERS_NEEDING_PRICING")
    filter = `${filter} and items/any(i: i/unitPrice eq 0) and (orderStatus ne '${OrderStatus.CANCELLED}' and orderStatus ne '${OrderStatus.CANCELED}' and orderStatus ne '${OrderStatus.INVOICED}')`;
  if (type !== null && type !== "ORDERS_NEEDING_PRICING")
    filter = `${filter} and modeofDelivery eq '${type}'`;

  let apiUrl = "";
  const skip = (page - 1) * pageSize;
  let orderBy = "utcDeliveryDateTime asc";

  if (sorting && sorting.length > 0) {
    if (sorting[0].id === "deliverydatetime") {
      orderBy = `utcDeliveryDateTime ${sorting[0].desc ? "desc" : "asc"}`;
    } else
      orderBy = `${sorting[0].id.replace(".", "/")} ${
        sorting[0].desc ? "desc" : "asc"
      }`;
  }

  apiUrl = `${PARTNER_API_BASE}/orders?$count=true&facet=supplier/friendlyName,count:0,sort:value&$filter=${filter}&$orderby=${orderBy}&$skip=${skip}&$top=${pageSize}`;

  if (search && search.trim() !== "") {
    const escapedSearch = search.trim().replace(/'/g, "''");
    apiUrl = `${apiUrl}&search=${encodeURIComponent(escapedSearch)}`;
  }

  const response = await http({
    url: apiUrl,
    method: "GET",
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });

  const { status, data } = response;

  return {
    status,
    data,
  };
};

ApiService.getActionRequiredVendorOrders = async (params) => {
  const { token, startDate, endDate } = params;
  let lessOperator = "le";
  let sdate = startDate;
  let edate = endDate;
  if (startDate === "") {
    sdate = moment().format("YYYY-MM-DD");
  }

  if (endDate === "") {
    edate = moment(sdate).add("days", 30).format("YYYY-MM-DD");
    lessOperator = "le";
  }

  let apiUrl = `${PARTNER_API_BASE}/orders?$count=true&$filter=(deliverydatetime ge '${sdate}' and deliverydatetime ${lessOperator} '${edate}') and (vendorStatus eq 0)&$orderby=deliverydatetime asc`;
  return await http({
    url: apiUrl,
    method: "GET",
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.recordVendorStatus = async (params) => {
  const { salesOrderId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesorder/${salesOrderId}`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.recordVendorActionFromEmail = async (params) => {
  const { salesOrderId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesorder/${salesOrderId}/recordVendorAction`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
    body: params,
  });
};

ApiService.validateVendorOrderStatus = async (params) => {
  const { salesOrderId, revision, token } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${encodeURIComponent(
      salesOrderId,
    )}/validateStatus/${encodeURIComponent(revision)}?token=${encodeURIComponent(token)}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.recordKitchenStatus = async (params) => {
  const { salesOrderId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesorder/${salesOrderId}/recordKitchenStatus`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.cancellationOrderRequest = async (params) => {
  const { salesOrderId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesorder/${salesOrderId}/cancel`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.markOrderDelivered = async (params) => {
  const { salesOrderId, dataAreaId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${salesOrderId}/delivered`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: {
      dataAreaId,
    },
  });
};

ApiService.generateDeeplink = async (params) => {
  const { salesId, dataAreaId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${salesId}/deeplink`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: {
      dataAreaId,
    },
  });
};

ApiService.getIntegrationOrders = async (params) => {
  const { token, invalidDateOrders } = params;
  let yesterdayDate = moment().subtract(1, "days").format("YYYY-MM-DD");
  let futureTime = moment().add(1, "hours").format("YYYY-MM-DDTHH:mm:ss[Z]");

  let apiUrl = "";

  if (invalidDateOrders)
    apiUrl = `${PARTNER_API_BASE}/orders?$count=true&$filter=((isProcessingStopped eq true and integrationStatus eq 'HighTouch' and deliverydatetime eq null) and (orderStatus ne 'Cancelled'))&$orderby=utcDeliveryDateTime asc`;
  else
    apiUrl = `${PARTNER_API_BASE}/orders?$count=true&$filter=(((isProcessingStopped eq true and integrationStatus eq 'HighTouch') or (search.in(integrationStatus,'HighTouch,NoTouch') and orderStatus eq 'Draft' and isProcessingStopped ne null) or (integrationStatus eq 'NoTouch' and isProcessingStopped eq true and lastModifiedDate gt '${futureTime}')) and orderStatus ne 'Cancelled' and deliverydatetime ne null and deliverydatetime gt '${yesterdayDate}')&$orderby=utcDeliveryDateTime asc`;

  apiUrl = `${apiUrl}&$skip=0&$top=5000`;

  return await http({
    url: apiUrl,
    method: HttpMethod.GET,
    header: {
      "api-key": SEARCH_INDEX_API_KEY,
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.getApplicationUsers = async (params) => {
  const { search } = params;
  return await http({
    url: `${PORTAL_API_BASE}/app/${PARTNER_API_CLIENT_ID}/users?search=${search}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomerByOktaId = async (params) => {
  const { oktaUserId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${oktaUserId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomerPricingFlag = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/pricing`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.switchCustomerAccount = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/customer/switch`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.getIntegrationOrderUserView = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/userViews/${userId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getVendorOrderUserView = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/userViews/vendor/${userId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.saveIntegrationOrderUserView = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/userViews`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.getOrderRevision = async (params) => {
  const { id } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${id}/revisions`,
    method: HttpMethod.GET,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getSalesOrderVendorRevisionHistory = async (params) => {
  const { id } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${id}/vendor/history`,
    method: HttpMethod.GET,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.addSalesOrderNotes = async (params) => {
  const { note, id } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${id}/notes`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: { notes: note },
  });
};

ApiService.getCustomers = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/customer`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomersWithPagination = async (params) => {
  const { offset, limit, search, type, status, sortBy, sortOrder } = params;

  let url = `${PORTAL_API_BASE}/customer/paginate?offset=${offset}&limit=${limit}`;

  if (search && search.trim() !== "") {
    url += `&search=${search}`;
  }

  if (type && type.toString().trim() !== "") {
    url += `&type=${type}`;
  }

  if (status && status.toString().trim() !== "") {
    url += `&status=${status === "enabled" ? 1 : 2}`;
  }

  if (sortBy && sortOrder) {
    url += `&sortBy=${encodeURIComponent(sortBy)}&sortOrder=${encodeURIComponent(sortOrder)}`;
  }

  return await http({
    url,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.addCustomer = async (params) => {
  const { formData } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer`,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: formData,
  });
};

ApiService.importD365Customer = async (params) => {
  const { partyId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/import/${partyId}`,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.migrateCustomer = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/customer/migrate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: params,
  });
};

ApiService.getD365CustomerDetails = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/d365/${customerId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomerIntegrators = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/integrators`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomerOrderEmails = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/orderEmails`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getUserIntegrations = async (params) => {
  const { customerId, userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user/${userId}/integration`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.updateUserIntegration = async (params) => {
  const { customerId, userId, integratorId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user/integration`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: { userId, integratorId },
  });
};

ApiService.addD365Customer = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/customer/d365`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: params,
  });
};

ApiService.addExternalOperatorMapping = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/externalOperatorMapping`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
    method: HttpMethod.POST,
    body: params,
  });
};

ApiService.editCustomer = async (params) => {
  const { formData } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${formData.get("customerId")}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.PUT,
    body: formData,
  });
};

ApiService.updateCustomerCategory = async (params) => {
  const { customerId, bcCategoryId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/category`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      bcCategoryId,
    },
  });
};

ApiService.updateCustomerStatus = async (params) => {
  const { customerId, status, userName, userEmail, message } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/status`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      status,
      userName,
      userEmail,
      message,
    },
  });
};

ApiService.updateCustomerPricing = async (params) => {
  const { customerId, showPricing, userName, userEmail, message } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/pricing`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      showPricing,
      userName,
      userEmail,
      message,
    },
  });
};

ApiService.updateCustomerIntegrator = async (params) => {
  const { customerId, integrators } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/integrators`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      integrators,
    },
  });
};

ApiService.updateCustomerOrderEmails = async (params) => {
  const { customerId, emails } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/orderEmails`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      orderEmails: emails,
    },
  });
};

ApiService.deleteCustomer = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.DELETE,
  });
};

ApiService.getUsers = async (params) => {
  const { customerId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.emailCheck = async (params) => {
  const { email } = params;
  return await http({
    url: `${PORTAL_API_BASE}/email-check/${email}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.getAdmins = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/users/admins`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getUserDetails = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/partner-landing/user/${userId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.setPassword = async (params) => {
  const { userId, password } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/setPassword`,
    method: HttpMethod.POST,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      "Content-Type": "application/json",
    },
    body: {
      password,
    },
  });
};

ApiService.approveRejectAccessRequest = async (params) => {
  const { status, customerId, userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user/${userId}/recordRequestAccess`,
    method: HttpMethod.POST,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      "Content-Type": "application/json",
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: {
      status,
    },
  });
};

ApiService.requestAccess = async (params) => {
  const {
    email,
    firstName,
    lastName,
    additionalDetails,
    partnerId,
    externalOperatorId,
    redirectUrl,
  } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/request-access`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
    method: HttpMethod.POST,
    body: {
      firstName,
      lastName,
      email,
      additionalDetails,
      partnerId,
      externalOperatorId,
      redirectUrl,
    },
  });
};

ApiService.addUser = async (params) => {
  const {
    customerId,
    firstName,
    lastName,
    email,
    role,
    fbos,
    connectToBC,
    active,
  } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      firstName,
      lastName,
      email,
      role,
      fbos,
      connectToBC,
      active,
    },
  });
};

ApiService.addAdmin = async (params) => {
  const { firstName, lastName, email } = params;
  return await http({
    url: `${PORTAL_API_BASE}/users/admins`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      firstName,
      lastName,
      email,
    },
  });
};

ApiService.activateUser = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/activate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.deactivateUser = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/deactivate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.unlockUserAccount = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/unlock`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.resetUserPassword = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/reset-password`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.reactivateUser = async (params) => {
  const { userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/user/${userId}/reactivate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.editUser = async (params) => {
  const { firstName, lastName, customerId, userId, role, fbos, connectToBC } =
    params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user/${userId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.PUT,
    body: {
      firstName,
      lastName,
      role,
      fbos,
      connectToBC,
    },
  });
};

ApiService.editAdmin = async (params) => {
  const { firstName, lastName, adminId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/users/admins/${adminId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.PUT,
    body: {
      firstName,
      lastName,
    },
  });
};

ApiService.deleteAdmin = async (params) => {
  const { adminId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/users/admins/${adminId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.DELETE,
  });
};

ApiService.activateAdmin = async (params) => {
  const { adminId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/users/admins/${adminId}/activate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.deactivateAdmin = async (params) => {
  const { adminId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/users/admins/${adminId}/deactivate`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
  });
};

ApiService.deleteUser = async (params) => {
  const { customerId, userId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/${customerId}/user/${userId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.DELETE,
  });
};

ApiService.getPermissions = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/permissions`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getIntegratorAdminRole = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/roles/integratorCustomer/admin`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.getRoles = async (params) => {
  const { type } = params;
  return await http({
    url: `${PORTAL_API_BASE}/roles/${type}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.addRole = async (params) => {
  const { name, description, permissions, type } = params;
  return await http({
    url: `${PORTAL_API_BASE}/roles`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.POST,
    body: {
      name,
      description,
      permissions,
      type,
    },
  });
};

ApiService.editRole = async (params) => {
  const { name, description, permissions, roleId, type } = params;
  return await http({
    url: `${PORTAL_API_BASE}/roles/${roleId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.PUT,
    body: {
      name,
      description,
      permissions,
      type,
    },
  });
};

ApiService.deleteRole = async (params) => {
  const { roleId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/roles/${roleId}`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    method: HttpMethod.DELETE,
  });
};

ApiService.getInvoices = function (params) {
  const data = fetch({
    url: "/invoices",
    method: "get",
    params,
  });
  return data.invoices;
};

ApiService.forgotPassword = async (params) => {
  const { email } = params;
  return await http({
    url: `${PORTAL_API_BASE}/forgot-password`,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
    method: HttpMethod.POST,
    body: {
      email,
    },
  });
};

ApiService.getKitchenContacts = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/kitchenContact`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getKitchens = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/kitchenContact/kitchens`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getIntegratorByAlias = async (params) => {
  const { alias } = params;
  return await http({
    url: `${PORTAL_API_BASE}/customer/alias/${alias}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getBCCategories = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/bc/categories`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getIntegrators = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/customer/integrators`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getCustomerFacets = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/customer/facets`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getNonOperationalVendors = async () => {
  return await http({
    url: `${PORTAL_API_BASE}/non-operational-vendors`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.getPostalAddresses = async (params) => {
  return await http({
    url: `${PORTAL_API_BASE}/postalAddress`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: params,
  });
};

ApiService.getCateringApiToken = async () => {
  return await http({
    url: `${CATERING_API_BASE_URL}/token`,
    method: HttpMethod.POST,
    header: {
      client_id: CATERING_API_CLIENT_ID,
      client_secret: CATERING_API_CLIENT_SECRET,
      "Content-Type": "application/json",
    },
    body: {
      grant_type: "client_credentials",
      scope: "api:read",
    },
  });
};

ApiService.validatePartnerAndOperator = async (params) => {
  const { token, externalOperatorId } = params;
  return await http({
    url: `${CATERING_API_BASE_URL}/partner/validate?externalOperatorId=${externalOperatorId}`,
    method: HttpMethod.GET,
    header: {
      Authorization: `Bearer ${token}`,
    },
  });
};

ApiService.getFbosByAirport = async (params) => {
  let { airport, token } = params;

  return await http({
    url: `${CATERING_API_BASE_URL}/fbos/${airport}`,
    method: HttpMethod.GET,
    header: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const updateBCCustomerMessage = async (params) => {
  const { bcOrderId, request } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${bcOrderId}/updateCustomerMessage`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: request,
  });
};

ApiService.updateOrderHeader = async (params) => {
  const { orderId, token, request, metaFieldRequest } = params;
  const response = await http({
    url: `${CATERING_API_BASE_URL}/orders/${orderId}`,
    method: HttpMethod.PATCH,
    header: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: request,
  });

  const { bcOrderId, ...rest } = metaFieldRequest;

  await updateBCCustomerMessage({
    token,
    bcOrderId,
    request: rest,
  });

  return response;
};

ApiService.trackOrder = async (params) => {
  const { orderNumber, tripNumber, tailNumber, date } = params;
  let filter = "";
  if (orderNumber && orderNumber.trim() !== "") {
    filter = `$filter=(salesOrderId eq '${orderNumber}') or (bcOrderNumber eq '${orderNumber}') `;
  } else {
    const endDate = moment(date).add("days", 1).format("YYYY-MM-DD");
    const tripDetailsFilters = [];

    if (tailNumber && tailNumber !== "")
      tripDetailsFilters.push(
        `search.ismatch('${tailNumber}', 'flightDetails/tailNumber')`,
      );

    if (tripNumber && tripNumber !== "")
      tripDetailsFilters.push(
        `search.ismatch('${tripNumber}', 'flightDetails/flightReference')`,
      );

    filter = `$filter=${tripDetailsFilters.join(
      " and ",
    )} and ((deliverydatetime ge '${date}') and (deliverydatetime le '${endDate}'))`;
  }

  let apiUrl = `${PORTAL_API_BASE}/trackOrder`;

  return await http({
    url: apiUrl,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
    body: {
      filter,
    },
  });
};

ApiService.getGlobalSettings = async (params) => {
  const { type } = params;
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/${type}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
    },
  });
};

ApiService.updateAdminReporting = async (values) => {
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/adminReporting`,
    method: HttpMethod.POST,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: { values },
  });
};

ApiService.getAnnouncements = async () => {
  const token = store.getState().auth?.token;
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/announcements`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

ApiService.createAnnouncement = async (announcement) => {
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/announcements`,
    method: HttpMethod.POST,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: announcement,
  });
};

ApiService.updateAnnouncement = async ({ id, ...announcement }) => {
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/announcements/${id}`,
    method: HttpMethod.PUT,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: announcement,
  });
};

ApiService.deleteAnnouncement = async (id) => {
  return await http({
    url: `${PORTAL_API_BASE}/appSettings/announcements/${id}`,
    method: HttpMethod.DELETE,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.getPowerBIReport = async (params) => {
  const { reportId } = params;
  return await http({
    url: `${PORTAL_API_BASE}/powerbi/embed/${reportId}`,
    method: HttpMethod.GET,
    header: {
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
  });
};

ApiService.updateKitchenProcessingStatus = async (params) => {
  const { salesOrderId, dataAreaId, status } = params;
  return await http({
    url: `${PORTAL_API_BASE}/salesOrder/${salesOrderId}/updateKitchenProcessingStatus`,
    method: HttpMethod.POST,
    header: {
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": CP_UAM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${store.getState().auth?.token}`,
    },
    body: {
      dataAreaId,
      status: status,
    },
  });
};

export default ApiService;
